import React from "react";
import { Box, Skeleton, useTheme } from "@mui/material";

// The Radar page's geometry, owned here (the eagerly-loaded chunk) so the
// skeleton and the lazily-loaded page can never drift apart: the heatmap
// board's height and the theme pulse grid's row height. RadarTab imports
// both.
export const RADAR_HEATMAP_HEIGHT = {
  xs: 560,
  sm: 540,
  md: "clamp(460px, 62dvh, 660px)",
} as const;
export const RADAR_THEME_ROW_HEIGHTS = { xs: 200, sm: 230, lg: 250, xl: 270 } as const;

interface HeaderSkeletonProps {
  titleWidth: number;
  skeletonBg: string;
}

// A section header's rail: the title, and the horizon + Cap/Equal pill pair
// (36px tall — it sets the row's height from sm up). Below sm the pills
// wrap onto a full row of their own beneath the 28px title line, exactly as
// RadarTab's headers do.
const HeaderSkeleton: React.FC<HeaderSkeletonProps> = ({ titleWidth, skeletonBg }) => (
  <>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: { xs: 28, sm: 36 },
        mb: { xs: 1, sm: 1.25 },
      }}
    >
      <Skeleton
        variant="rounded"
        animation="wave"
        height={28}
        sx={{ width: "100%", maxWidth: titleWidth, borderRadius: 1, bgcolor: skeletonBg }}
      />
      <Skeleton
        variant="rounded"
        animation="wave"
        height={36}
        sx={{
          width: 340,
          flexShrink: 0,
          borderRadius: 1,
          bgcolor: skeletonBg,
          display: { xs: "none", sm: "block" },
        }}
      />
    </Box>
    <Skeleton
      variant="rounded"
      animation="wave"
      height={36}
      sx={{ mb: 1.25, borderRadius: 1, bgcolor: skeletonBg, display: { xs: "block", sm: "none" } }}
    />
  </>
);

// Mirrors the Radar page geometry — heatmap section header + board, then
// the theme pulse header and map grid — so real content lands without a
// layout shift.
const RadarSkeleton: React.FC = () => {
  const theme = useTheme();
  const skeletonBg = theme.palette.mode === "dark" ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.09)";

  return (
    <Box>
      <HeaderSkeleton titleWidth={220} skeletonBg={skeletonBg} />
      <Skeleton
        variant="rounded"
        animation="wave"
        sx={{
          height: RADAR_HEATMAP_HEIGHT,
          borderRadius: 1.5,
          bgcolor: skeletonBg,
          mb: { xs: 4, md: 5 },
        }}
      />
      <HeaderSkeleton titleWidth={180} skeletonBg={skeletonBg} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)", xl: "repeat(4, 1fr)" },
          gridAutoRows: RADAR_THEME_ROW_HEIGHTS,
          gap: { xs: 2.5, md: 3 },
        }}
      >
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton
            key={index}
            variant="rounded"
            animation="wave"
            sx={{ height: "100%", borderRadius: 1, bgcolor: skeletonBg }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default RadarSkeleton;
